import { useCallback } from 'react'
import { toast } from 'sonner'
import type { EditableThemeTokensByVariant } from '@/shared/theme/theme-config'
import type * as Types from '@/graphql/schema'
import { mapKebabTokensToCamel } from '@/shared/theme/theme-tokens-mapping'
import { useUpdateWorkspaceThemeMutation } from '@/graphql/mutations/updateWorkspaceThemeMutation.gql.generated'
import { CurrentUserDocument } from '@/graphql/queries/currentUserQuery.gql.generated'
import { resolveActionError } from '@/shared/lib/action-error'

export function useSaveWorkspaceBranding({
  resetCustomTokens,
  workspaceId,
}: {
  resetCustomTokens: () => void
  workspaceId: string | undefined
}) {
  const [updateWorkspaceTheme, { loading: isSaving }] =
    useUpdateWorkspaceThemeMutation({
      refetchQueries: [{ query: CurrentUserDocument }],
    })

  const save = useCallback(
    async (draft: EditableThemeTokensByVariant) => {
      if (!workspaceId) {
        return
      }

      const camelTokens = mapKebabTokensToCamel(draft)

      try {
        await updateWorkspaceTheme({
          variables: {
            input: {
              workspaceId,
              customThemeTokens:
                camelTokens as unknown as Types.WorkspaceThemeTokensInput,
            },
          },
        })
        resetCustomTokens()
        toast.success('Workspace branding saved')
      } catch (error) {
        toast.error(
          resolveActionError(error, 'Failed to save workspace branding')
            .message,
        )
      }
    },
    [updateWorkspaceTheme, resetCustomTokens, workspaceId],
  )

  const reset = useCallback(async () => {
    if (!workspaceId) {
      return
    }

    try {
      await updateWorkspaceTheme({
        variables: {
          input: {
            workspaceId,
            customThemeTokens: null,
          },
        },
      })
      resetCustomTokens()
      toast.success('Workspace branding reset')
    } catch (error) {
      toast.error(
        resolveActionError(error, 'Failed to reset workspace branding').message,
      )
    }
  }, [updateWorkspaceTheme, resetCustomTokens, workspaceId])

  return { save, reset, isSaving }
}
